// A transcript with a Translate button over it.
//
// Mira answers a client in the client's own language on purpose, so a large share
// of the conversations in this console are unreadable to the employee auditing
// them. This wraps `ConversationThread` with the control that fixes that, in ONE
// place, because the Conversation tab renders the thread from two call sites (the
// operator's write view and the read-only one) and a control added twice is a
// control that drifts.
//
// Deliberately not a toggle that hides itself once used: an operator reading an
// audit trail has to be able to get back to what was actually said, so the
// original is always one click away and the bar keeps saying which of the two is
// on screen.
import { Button, Spinner } from "@recruiter/shared";
import type { SupportChatMessage } from "@recruiter/shared/types";

import { useConversationTranslation } from "../lib/use-conversation-translation";
import { ConversationThread } from "./conversation-thread";
import "./translatable-conversation.css";

export function TranslatableConversation({
  projectId,
  messages,
  clientName,
}: {
  projectId: string;
  messages: SupportChatMessage[];
  clientName: string;
}) {
  const translation = useConversationTranslation(projectId, messages.length);
  const { status, pending, canAsk, overlay, showing, sourceLanguage, shortfall, staleBy, error } =
    translation;
  const ready = status === "ready";

  return (
    <div className="tc">
      <div className="tc__bar">
        {ready ? (
          <>
            <Button variant="ghost" tone="neutral" size="sm" onClick={translation.toggle}>
              {showing ? "Show original" : "Show translation"}
            </Button>
            {/* The retry the stale note below PROMISES. Without it that note is
                advice with no control behind it, and a translation that failed
                next to a loaded one has no way forward either. Free when the
                transcript has not changed: the server keys on a content digest. */}
            <Button
              variant="ghost"
              tone="neutral"
              size="sm"
              disabled={pending || !canAsk}
              onClick={translation.translate}
            >
              {pending ? "Translating" : "Translate again"}
            </Button>
            {pending ? <Spinner size="sm" /> : null}
            <span className="tc__note">
              {showing
                ? sourceLanguage
                  ? `Translated from ${sourceLanguage} by a model, for reading only.`
                  : "Translated by a model, for reading only."
                : "Showing what was actually written."}
            </span>
          </>
        ) : (
          <>
            <Button
              variant="ghost"
              tone="neutral"
              size="sm"
              disabled={status === "pending" || !canAsk || messages.length === 0}
              onClick={translation.translate}
            >
              {status === "pending" ? "Translating" : "Translate to English"}
            </Button>
            {status === "pending" ? <Spinner size="sm" /> : null}
          </>
        )}
      </div>

      {error ? <p className="tc__warn">{error}</p> : null}
      {ready && showing && shortfall ? <p className="tc__warn">{shortfall}</p> : null}
      {ready && showing && staleBy > 0 ? (
        <p className="tc__warn">
          {staleBy === 1 ? "1 message arrived" : `${staleBy} messages arrived`} after this was
          translated, and {staleBy === 1 ? "it is" : "they are"} still in the original language.
          Translate again to include {staleBy === 1 ? "it" : "them"}.
        </p>
      ) : null}

      <ConversationThread messages={messages} clientName={clientName} translations={overlay} />
    </div>
  );
}
